import {useState} from "react";
import {Check} from "lucide-react";
import {useTranslation} from "react-i18next";
import {ServiceGroup, ServiceWithGroupId} from "@/models/service";
import {cn} from "@/lib/utils";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,} from "@/components/ui/command";
import {Popover, PopoverContent, PopoverTrigger,} from "@/components/ui/popover";

type SingleServiceSelectorProps = {
    services: ServiceWithGroupId[];
    groups: ServiceGroup[];
    selectedServiceId: string;
    onChange: (serviceId: string) => void;
    placeholder?: string;
    className?: string;
    invalid?: boolean;
    'data-testid'?: string;
};

function SingleServiceSelector({
                                   services,
                                   groups,
                                   selectedServiceId,
                                   onChange,
                                   placeholder = "Service auswählen",
                                   className,
                                   invalid = false,
                                   'data-testid': dataTestId,
                               }: SingleServiceSelectorProps) {
    const { t } = useTranslation('members');
    const [open, setOpen] = useState(false);

    const selectedService = services.find(service => service.id === selectedServiceId);

    const servicesByGroup = groups.map(group => ({
        group,
        services: services.filter(service =>
            service.serviceGroupId === group.id && !service.isDeleted
        )
    })).filter(group => group.services.length > 0);

    const ungroupedServices = services.filter(service =>
        !service.serviceGroupId && !service.isDeleted
    );

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        "relative w-full appearance-none rounded-lg border bg-white px-3 py-2 text-left",
                        "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6",
                        "border-zinc-950/10 hover:border-zinc-950/20",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500",
                        "dark:bg-zinc-800 dark:text-white dark:border-white/10 dark:hover:border-white/20",
                        invalid && "border-red-500 hover:border-red-500 dark:border-red-500 dark:hover:border-red-500",
                        "disabled:opacity-50 disabled:bg-zinc-50 dark:disabled:bg-zinc-900",
                        className
                    )}
                    aria-expanded={open}
                    data-testid={dataTestId}
                >
          <span className="block truncate">
            {selectedService?.name || placeholder}
          </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <svg
                className="size-5 stroke-zinc-500 sm:size-4 dark:stroke-zinc-400"
                viewBox="0 0 16 16"
                aria-hidden="true"
                fill="none"
            >
              <path d="M5.75 10.75L8 13L10.25 10.75" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.25 5.25L8 3L5.75 5.25" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[200px]"
                align="start"
                sideOffset={5}
            >
                <Command className="rounded-lg border border-zinc-200 bg-white shadow-md dark:border-zinc-800 dark:bg-zinc-900">
                    <CommandInput
                        placeholder={t("memberForm.fields.services.searchPlaceholder")}
                        className="h-10 px-3 py-2 text-sm border-b border-zinc-200 dark:border-zinc-800"
                    />
                    <CommandList className="max-h-60 overflow-auto py-1">
                        <CommandEmpty className="py-3 text-center text-sm text-zinc-500 dark:text-zinc-400">
                            {t("memberForm.fields.services.noOptions")}
                        </CommandEmpty>
                        {servicesByGroup.map(({ group, services }) => (
                            <CommandGroup
                                key={group.id}
                                heading={group.name}
                                className="px-2 py-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400"
                            >
                                {services.map(service => (
                                    <CommandItem
                                        key={service.id}
                                        value={service.name}
                                        onSelect={() => {
                                            onChange(service.id === selectedServiceId ? "" : service.id);
                                            setOpen(false);
                                        }}
                                        className="flex cursor-pointer items-center rounded-md px-2 py-1.5 text-sm text-zinc-900 hover:bg-zinc-100 data-[selected=true]:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-800 dark:data-[selected=true]:bg-zinc-800"
                                        data-testid="service-option"
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4 text-blue-600 dark:text-blue-400",
                                                selectedServiceId === service.id
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        <span>{service.name}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        ))}

                        {ungroupedServices.length > 0 && (
                            <CommandGroup
                                heading={t("memberForm.fields.services.ungrouped")}
                                className="px-2 py-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400"
                            >
                                {ungroupedServices.map(service => (
                                    <CommandItem
                                        key={service.id}
                                        value={service.name}
                                        onSelect={() => {
                                            onChange(service.id === selectedServiceId ? "" : service.id);
                                            setOpen(false);
                                        }}
                                        className="flex cursor-pointer items-center rounded-md px-2 py-1.5 text-sm text-zinc-900 hover:bg-zinc-100 data-[selected=true]:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-800 dark:data-[selected=true]:bg-zinc-800"
                                        data-testid="service-option"
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4 text-blue-600 dark:text-blue-400",
                                                selectedServiceId === service.id
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        <span>{service.name}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default SingleServiceSelector;
